import React, { useState, useEffect } from 'react';
import { Box, Paper, List, ListItemButton, ListItemIcon, ListItemText, Typography, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ContactsIcon from '@mui/icons-material/Contacts';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EmailIcon from '@mui/icons-material/Email';
import { projectService } from '../../services/projectService';

const ProjectSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Could be :id (ProjectDetail) or :project (other views)
  const projectId = params.project || params.id;

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId && projectId !== 'new') {
      const fetchProject = async () => {
        try {
          const data = await projectService.getProject(projectId);
          setProject(data);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchProject();
    } else {
      setLoading(false);
    }
  }, [projectId]);

  if (!projectId || projectId === 'new') return null;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (!project) return null;

  const currentPath = location.pathname;

  const isSelected = (pathSegment) => {
    if (pathSegment === '') {
      return currentPath.endsWith(`/project/${projectId}`);
    }
    return currentPath.includes(`/project/${projectId}/${pathSegment}`);
  };

  const enabledFeatures = project.enabledFeatures || ['traffic'];

  let navItems = [
    { label: 'Project Settings', icon: <SettingsSuggestIcon />, path: '' },
    { label: 'Overview', icon: <DashboardIcon />, path: 'contactdashboard' },
  ];

  if (enabledFeatures.includes('crm')) {
    navItems = navItems.concat([
      { label: 'Contacts', icon: <ContactsIcon />, path: 'contact' },
      { label: 'Contact Properties', icon: <LocalOfferIcon />, path: 'contactproperty' },
      { label: 'Groups', icon: <GroupIcon />, path: 'group' },
    ]);
  }

  // Traffic specific
  navItems.push({ label: 'Visitors', icon: <VisibilityIcon />, path: 'anonymous' });

  if (enabledFeatures.includes('automations')) {
    navItems = navItems.concat([
      { label: 'Automations', icon: <SettingsSuggestIcon />, path: 'automation' },
      { label: 'Email Templates', icon: <EmailIcon />, path: 'emailtemplate' },
    ]);
  }

  let actionItems = [];
  if (enabledFeatures.includes('crm')) {
    actionItems = [
      { label: 'Add a new Contact', icon: <PersonAddIcon />, path: 'contact', disabled: true },
      { label: 'Create Contact Property', icon: <SaveIcon />, path: 'contactproperty/new' },
      { label: 'Create Group', icon: <GroupAddIcon />, path: 'group/new' },
      { label: 'Export Contacts', icon: <ImportExportIcon />, path: '#', disabled: true },
      { label: 'Import Contacts', icon: <CloudUploadIcon />, path: 'importcontact' },
    ];
  }

  const handleNav = (path) => {
    if (path === '#') return;
    navigate(`/project/${projectId}/${path}`);
  };

  return (
    <Paper elevation={1} sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ bgcolor: 'grey.100', p: 2, borderBottom: '1px solid #eee' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Analytics & CRM
        </Typography>
      </Box>
      <List disablePadding>
        {navItems.map((item) => (
          <ListItemButton
            key={item.label}
            selected={isSelected(item.path)}
            onClick={() => handleNav(item.path)}
            sx={{
              borderBottom: '1px solid #f5f5f5',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>

      {actionItems.length > 0 && (
        <>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderBottom: '1px solid #eee', borderTop: '1px solid #eee' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Actions
            </Typography>
          </Box>
          <List disablePadding>
            {actionItems.map((item) => (
              <ListItemButton
                key={item.label}
                onClick={() => handleNav(item.path)}
                disabled={item.disabled}
                sx={{ borderBottom: '1px solid #f5f5f5' }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
};

export default ProjectSidebar;
